/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  Threshold,
  MeterSize,
  ThresholdDisplay,
  getVisThresholdColor
} from '#utils/UNSAFE_meterUtils';
import { getClipPath, getPositiveAngle, Sector } from '#utils/PRIVATE_meterUtils';
import { getAngleExtentFromDelta, getStartAngleForValue } from './circleMeterUtils';
import { MeterProps } from './meterCircle.types';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { meterCircleVars } from './themes/MeterCircleContract.css';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { xUnits } from '#UNSAFE_Theme/utils';
export type MeterInfo = Required<
  Pick<MeterProps, 'min' | 'max' | 'value' | 'size' | 'startAngle' | 'angleExtent'>
> & {
  section: Sector;
  isRtl: boolean;
  innerRadius: number;
};

// reduce the max width of text by 8px. There were some inconsistency on exact
// truncation width between browsers, viewport, container width, which led to some ellipses
// bleeding out. Reducing the max width provides a buffer so that labels are truncated
// slightly before reaching the exact max width.
const TRUCATION_THRESHOLD = xUnits(2);

//max left (right) offset for right (left) half circle for sized meter circle
export const SIZED_HALF_METER_HORIZ_OFFSET = xUnits(7.5);

// max ref line protrusion

const MAX_PROTUSION = 16;

/**
 * Returns the css variable corresponding to the default size of the meter circle.
 * @param size The size of the meter circle.
 * @returns The css variable corresponding to the default size of the meter circle.
 */
function getSizeVar(size: MeterSize, diameter?: number): string {
  return size
    ? size === 'fit'
      ? `${diameter}px`
      : meterCircleVars.sizes[size]
    : meterCircleVars.size;
}

/**
 * Returns the css variable corresponding to the default track size of the meter circle.
 * @param size The size of the meter circle.
 * @returns The css variable corresponding to the default track size of the meter circle.
 */
function getTrackSizeVar(size: MeterSize): string {
  return size ? meterCircleVars.trackSizes[size] : meterCircleVars.trackSize;
}

/**
 * Returns the calculated css variable string for the inner radius when given size and innerRadius of meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns {string} CSS varible string for the calculated inner radius of the meter circle.
 */
export function getInnerRadius(size: MeterSize, innerRadius?: number, diameter?: number): string {
  if (innerRadius != undefined) {
    return `(${getSizeVar(size, diameter)} * ${innerRadius / 2})`;
  }
  return `(${getSizeVar(size, diameter)} * 0.5 - ${getTrackSizeVar(size)})`;
}

/**
 * Returns the calculated css variable string for meter circle track for a given size and innerRadius of meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns {string} CSS variable for the calculated track size of the meter circle.
 */
export function getTrackSize(size: MeterSize, innerRadius?: number, diameter?: number): string {
  if (innerRadius != undefined) {
    return `(${getSizeVar(size, diameter)} * ${1 - innerRadius} / 2)`;
  }
  return getTrackSizeVar(size);
}

/**
 * Returns the array of calculated props for InnerCircle representing each threshold.
 * @param meterInfo MeterInfo object containing info about given meter circle.
 * @param thresholds thresholds array of the meter circle.
 * @param isTrackRendered Whether track is rendered.
 * @param trackColor The track color of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns Array of calculated props object for InnerCircle representing each threshold.
 */
export function getThresholdsProps(
  meterInfo: MeterInfo,
  thresholds: Threshold[],
  isTrackRendered: boolean,
  trackColor?: ColorProps['color'],
  innerRadius?: number,
  diameter?: number
): Array<{
  clipPath: string;
  color?: ColorProps['color'];
  section: Sector;
  size: string;
}> {
  const {
    min,
    max,
    startAngle,
    angleExtent,
    size,
    section,
    isRtl,
    innerRadius: innerDim
  } = meterInfo;
  const trackSize = `calc(${getTrackSize(size, innerRadius, diameter)})`;
  const thresholdProps = [];
  const validThresholds = thresholds
    .filter((threshold: Threshold) => threshold.max > min && threshold.max <= max)
    .sort((a, b) => a.max - b.max);
  const numOfThreshold = validThresholds.length;
  for (let i = 0; i < numOfThreshold; i++) {
    const threshold = validThresholds[i];
    const startValue = i === 0 ? min : validThresholds[i - 1].max;
    const diff = i === 0 ? threshold.max : threshold.max - validThresholds[i - 1].max;

    let thresholdStart = getStartAngleForValue(
      startValue,
      min,
      max,
      startAngle,
      angleExtent,
      isRtl
    );
    let thresholdExtent = getAngleExtentFromDelta(diff, min, max, angleExtent);

    const { startOffset, extentOffset } = getThresholdsBuffer(
      innerDim,
      angleExtent,
      i === 0,
      threshold.max === max,
      isRtl
    );
    const clipPath = getClipPath(
      thresholdStart + startOffset,
      thresholdExtent + extentOffset,
      isRtl,
      section
    );

    thresholdProps.push({
      clipPath,
      color: getVisThresholdColor(threshold.color),
      size: trackSize,
      section
    });

    if (i === numOfThreshold - 1 && threshold.max < max && isTrackRendered) {
      thresholdStart = thresholdStart + (isRtl ? 1 : -1) * thresholdExtent;
      thresholdExtent = getAngleExtentFromDelta(max - threshold.max, min, max, angleExtent);
      const { startOffset, extentOffset } = getThresholdsBuffer(
        innerDim,
        angleExtent,
        false,
        true,
        isRtl
      );
      thresholdProps.push({
        clipPath: getClipPath(
          thresholdStart + startOffset,
          thresholdExtent + extentOffset,
          isRtl,
          section
        ),
        color: trackColor,
        size: trackSize,
        section
      });
    }
  }
  return thresholdProps;
}

/**
 * Returns the calculated props for InnerCircle representing the track.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param trackColor The color of the track.
 * @param istrackRendered Whether the track is rendered.
 * @param thresholdDisplay Specifies whether the current threshold is displayed in the track, on the indicator, or if all thresholds are displayed in the track.
 * @param innerRadius The innerRadius of the meter circle.
 * @param thresholds thresholds array of the meter circle.
 * @returns An object with calculated props for InnerCircle that represents the track.
 */
export function getTrackProps(
  meterInfo: MeterInfo,
  isTrackRendered: boolean,
  thresholdDisplay: ThresholdDisplay,
  trackColor?: ColorProps['color'],
  innerRadius?: number,
  thresholds?: Threshold[],
  diameter?: number
): {
  section: Sector;
  color?: ColorProps['color'];
  clipPath: string;
  size: string;
} {
  const { startAngle, angleExtent, size, section, isRtl } = meterInfo;
  const color = !isTrackRendered
    ? 'transparent'
    : thresholdDisplay === 'all' && thresholds
    ? `${colorSchemeVars.dvt.contrastLine}`
    : trackColor;
  return {
    section,
    color,
    clipPath: getClipPath(startAngle, angleExtent, isRtl, section),
    size: `calc(${getTrackSize(size, innerRadius, diameter)})`
  };
}

/**
 * Returns the calculated props for InnerCircle representing the indicator.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param indicatorSize Relative thickness of the indicator to the track.
 * @param innerRadius The innerRadius of the meter circle.
 * @returns An object with calculated props for InnerCircle that represents the indicator.
 */
export function getIndicatorProps(
  meterInfo: MeterInfo,
  indicatorSize: number,
  innerRadius?: number,
  fitDiameter?: number
) {
  const { value, min, max, startAngle, angleExtent, size, section, isRtl } = meterInfo;
  const valueExtent = ((value - min) * angleExtent) / (max - min);
  const validIndicatorSize = Math.min(Math.max(0, indicatorSize), 1);
  const isHoriz = section === 'left' || section === 'right';
  const isVert = section === 'top' || section === 'bottom';
  const valueTrackSize = `calc(${getTrackSize(
    size,
    innerRadius,
    fitDiameter
  )} * ${validIndicatorSize})`;
  const diameter = `${getSizeVar(
    size,
    fitDiameter
  )} - ((1 - ${validIndicatorSize}) * ${getTrackSize(size, innerRadius, fitDiameter)})`;
  const radius = `(${diameter}) / 2`;
  return {
    section,
    width: isHoriz ? `calc(${radius})` : `calc(${diameter})`,
    height: isVert ? `calc(${radius})` : `calc(${diameter})`,
    size: valueTrackSize,
    clipPath: getClipPath(startAngle, valueExtent, isRtl, section)
  };
}

/**
 * Returns the threshold offsets to create the whitespace between the thresholds.
 * @param innerRadius The innerRadius of the meter circle.
 * @param angleExtent The angleExtent of the meter circle.
 * @param firstThreshold Whether the current threshold is the first threshold.
 * @param lastThreshold  Whether the current threshold is the last threshold.
 * @param isRtl Whether the document reading mode is 'rtl'
 * @returns The offsets (startOffset and extentOffset) to add to calculated threshold startAngle and threhsold extent.
 */
function getThresholdsBuffer(
  innerRadius: number,
  angleExtent: number,
  firstThreshold: boolean,
  lastThreshold: boolean,
  isRtl: boolean
): {
  startOffset: number;
  extentOffset: number;
} {
  if (!innerRadius) {
    return { startOffset: 0, extentOffset: 0 };
  }
  const offset = 360 / (2 * Math.PI * innerRadius);
  let startOffset = (offset / 2) * (isRtl ? 1 : -1);
  let extentOffset = -1 * offset;

  if (firstThreshold && angleExtent < 360) {
    startOffset = 0;
    extentOffset = -0.5 * offset;
  }

  if (lastThreshold && angleExtent < 360) {
    extentOffset = -0.5 * offset;
  }
  return { startOffset, extentOffset };
}

export function getWidthAndHeight(width: number, height: number, section: Sector) {
  if (section === 'top' || section === 'bottom') {
    return {
      height: Math.min(height, width / 2),
      width: Math.min(height, width / 2) * 2
    };
  } else if (section === 'left' || section === 'right') {
    return {
      width: Math.min(width, height / 2),
      height: Math.min(width, height / 2) * 2
    };
  } else {
    return {
      width: Math.min(width, height),
      height: Math.min(width, height)
    };
  }
}

/**
 * Returns the x and y offset for absolute position of reference line.
 */
export function getReferenceLineXYOffset(
  centerCoord: { x: number; y: number },
  angle: number,
  radius: string
) {
  const yOffset = `calc(${centerCoord.y * 100}% - ((${Math.sin(
    (angle * 2 * Math.PI) / 360
  )} * ${radius} )))`;
  const xOffset = `calc(${centerCoord.x * 100}%  + ((${Math.cos(
    (angle * 2 * Math.PI) / 360
  )} * ${radius} )))`;
  return { xOffset, yOffset };
}

/**
 * Returns the translation offset for the reference line label.
 */
export function getLabelTranslateOffset(
  ag: number,
  section: Sector,
  halfMeterOffset: string | number
) {
  let horizTranslate = '-50%';
  const angle = getPositiveAngle(ag);

  if (angle === 90 || angle === 270) {
    halfMeterOffset =
      typeof halfMeterOffset === 'string' ? halfMeterOffset : `${Math.abs(halfMeterOffset)}px`;
    if (section === 'left') {
      horizTranslate = `min(-50%, calc(-100% + ${halfMeterOffset}))`;
    } else if (section === 'right') {
      horizTranslate = `max(-50%, calc(-1 *  ${halfMeterOffset}))`;
    }
  }

  // these numbers are from the meter circle spec.

  if (angle <= 72 || angle >= 288) {
    return `0 -50%`;
  } else if (angle >= 108 && angle <= 252) {
    return `-100% -50%`;
  } else if (angle <= 108 && angle >= 72) {
    return `${horizTranslate} -100%`;
  } else {
    return `${horizTranslate} 0`;
  }
}

/**
 * The maxiumum width of for the reference line label in meter circle.
 */
export function getMaxWidth(sizeVar: string, angle: number, radius: string) {
  angle = getPositiveAngle(angle);
  if ((angle < 108 && angle > 72) || (angle < 288 && angle > 252)) {
    return `calc(${sizeVar} * 1.4)`;
  }

  return `calc( (${sizeVar} * 0.7 ) - (${Math.abs(
    Math.cos((angle * 2 * Math.PI) / 360)
  )} * ${radius} ))`;
}

/**
 * Returns the maximum width of label for a given angled reference line.
 */
export function getFitMaxWidth(sector: Sector, width: number, angle: number, radius: number) {
  angle = getPositiveAngle(angle);
  if (sector === 'full' || sector === 'bottom' || sector === 'top') {
    if ((angle < 108 && angle > 72) || (angle < 288 && angle > 252)) {
      return `calc(${width}px - ${TRUCATION_THRESHOLD})`;
    }

    return `calc(${
      width / 2 - Math.abs(Math.cos((angle * 2 * Math.PI) / 360)) * radius
    }px - ${TRUCATION_THRESHOLD})`;
  } else {
    if (angle === 90 || angle === 270) {
      return `calc(${width}px - ${TRUCATION_THRESHOLD})`;
    }

    return `calc(${
      width / 2 + radius / 2 - radius * Math.abs(Math.cos((angle * 2 * Math.PI) / 360))
    }px - ${TRUCATION_THRESHOLD})`;
  }
}

/**
 * Returns the margin needed to accomodate reference line and label in a sized meter circle.
 */
export function getSizedMargin(
  size: string,
  sector: Sector,
  hasReferenceLine: boolean,
  hasReferenceLineLabel: boolean,
  maxRefLabelDims: { width: number; height: number }
) {
  let marginValue = '';

  if (!hasReferenceLine && !hasReferenceLine) {
    return '';
  }

  if (hasReferenceLineLabel) {
    marginValue = `calc(${meterCircleVars.size} / ${size === 'lg' || size === 'md' ? 3 : 2} )`;
  } else if (hasReferenceLine) {
    if (size === 'sm') {
      marginValue = xUnits(4);
    } else if (size === 'md') {
      marginValue = xUnits(3);
    } else if (size === 'lg') {
      marginValue = xUnits(2);
    }
  }

  if (sector === 'bottom') {
    return `${maxRefLabelDims.height / 2}px ${marginValue} ${marginValue} ${marginValue}`;
  } else if (sector === 'top') {
    return `${marginValue} ${marginValue} ${maxRefLabelDims.height / 2}px ${marginValue}`;
  } else if (sector === 'left') {
    return `${marginValue} ${SIZED_HALF_METER_HORIZ_OFFSET} ${marginValue} ${marginValue}`;
  } else if (sector === 'right') {
    return `${marginValue} ${marginValue} ${marginValue} ${SIZED_HALF_METER_HORIZ_OFFSET}`;
  } else {
    return marginValue;
  }
}
/**
 * Returns the breakpoint for a given width of the meter circle.
 */
export function getBreakPoint(width: number) {
  // these numbers are from the meter circle spec.
  if (width < 200) {
    return 'min';
  } else if (width < 320) {
    return 'xs';
  } else if (width < 360) {
    return 'sm';
  } else {
    return 'md';
  }
}

/**
 * Returns the translation needed to center the meter bar when
 * reference line and label is present.
 */
export function getCenterOffsets(section: Sector, overHang: number, isRtl: boolean) {
  const overHangOffset = section === 'full' ? overHang / 2 : overHang;
  if (section === 'top') {
    return isRtl ? '50% -50%' : '-50%, -50%';
  }
  return isRtl
    ? `calc(50% + ${overHangOffset}px), calc(-50% - ${overHangOffset}px)`
    : `calc(-50% - ${overHangOffset}px), calc(-50% - ${overHangOffset}px)`;
}

/**
 * Returns the horizontal offset for left and right half circle for fit meter circle.
 */
export function getFitHalfCircleOffset(
  width: number,
  radius: number,
  section: Sector,
  isRtl: boolean
) {
  const factor = (!isRtl && section === 'right') || (isRtl && section === 'left') ? -0.5 : 0.5;
  return (width - radius) / 2 + factor * MAX_PROTUSION;
}
